/**
 * Replaces native <select> popups with LTMS custom-select menus.
 * UI is portaled to document.body (like the dropdown menu) so Vue patch
 * does not fight foreign nodes between template siblings.
 */

import { applyViewportMenu, clipBelowModuleHeader } from '@/lib/overlayClip'
import { PAGE_ZOOM_EVENT, cssPxFromVisual } from '@/lib/pageZoom'

const ENHANCED = 'data-ltms-custom-select'
const UI_WRAP = 'data-ltms-select-ui'
const SKIP_CLOSEST =
  '.choices, .custom-select-wrap, .dp__menu, .vuefinder, [data-no-custom-select]'

function getUiWrap(select) {
  const linked = select?._ltmsSelectWrap
  if (
    linked?.isConnected &&
    linked.classList.contains('custom-select-wrap') &&
    linked._ltmsSelectSource === select
  ) {
    return linked
  }
  return null
}

function isInternalSelectMutation(mutation) {
  const target = mutation.target
  if (!(target instanceof Element)) return true

  if (target.closest?.('.custom-select-wrap, .custom-select-menu')) {
    return true
  }

  if (
    target instanceof HTMLSelectElement &&
    target.hasAttribute(ENHANCED) &&
    mutation.type === 'childList'
  ) {
    return true
  }

  if (
    mutation.type === 'attributes' &&
    target instanceof HTMLSelectElement &&
    target.hasAttribute(ENHANCED)
  ) {
    const name = mutation.attributeName
    if (
      name === 'class' ||
      name === 'data-ltms-invalid' ||
      name === 'disabled' ||
      name === 'value'
    ) {
      return true
    }
  }

  return false
}

/**
 * Select в фильтре колонки: триггер-оверлей остаётся в ячейке (ездит со скроллом),
 * а открытое меню — на body (см. openSelect), иначе overflow клипает список.
 *
 * @param {HTMLSelectElement | null | undefined} select
 * @returns {boolean}
 */
function isColumnFilterSelect(select) {
  return Boolean(select?.closest?.('.filter-dropdown, thead th'))
}

function clearWrapLayoutStyles(wrap) {
  if (!wrap) return
  wrap.style.position = ''
  wrap.style.top = ''
  wrap.style.left = ''
  wrap.style.right = ''
  wrap.style.bottom = ''
  wrap.style.inset = ''
  wrap.style.width = ''
  wrap.style.maxWidth = ''
  wrap.style.height = ''
  wrap.style.overflow = ''
  wrap.style.boxSizing = ''
  wrap.style.visibility = ''
  wrap.style.pointerEvents = ''
  wrap.style.display = ''
  wrap.style.transition = ''
  wrap.style.zIndex = ''
  wrap.style.clipPath = ''
}

/** Статичный оверлей поверх native select внутри ячейки фильтра. */
function layoutColumnFilterWrap(select, wrap) {
  const host =
    select.closest('.filter-dropdown-bool-row') ||
    select.closest('.filter-dropdown') ||
    select.parentElement
  if (!host) return

  ensureHostPositioned(host)
  if (wrap.parentElement !== host) {
    host.appendChild(wrap)
  }

  clearWrapLayoutStyles(wrap)
  wrap.style.position = 'absolute'
  wrap.style.inset = '0'
  wrap.style.width = '100%'
  wrap.style.maxWidth = '100%'
  wrap.style.height = '100%'
  wrap.style.overflow = 'visible'
  wrap.style.boxSizing = 'border-box'
  wrap.style.zIndex = '2'
}

/**
 * @param {HTMLElement} host
 */
function ensureHostPositioned(host) {
  if (getComputedStyle(host).position !== 'static') return
  host.dataset.ltmsSelectHost = '1'
  host.style.position = 'relative'
}

/**
 * Формы / пагинация: оверлей absolute в колонке поверх native select.
 * Не fixed на body — при CSS zoom getBoundingClientRect и style.top расходятся,
 * поля «плывут» относительно лейблов. offset* в CSS px масштабонезависимы.
 *
 * @param {HTMLSelectElement} select
 * @param {HTMLElement} wrap
 */
function layoutFormSelectWrap(select, wrap) {
  const host = select.parentElement
  if (!host) return

  ensureHostPositioned(host)
  if (wrap.parentElement !== host) {
    select.insertAdjacentElement('afterend', wrap)
  }

  clearWrapLayoutStyles(wrap)
  showFloatingWrap(wrap)

  // offsetLeft/Top относительны offsetParent, не parentElement — при padding/zoom
  // иногда уезжают. Разница getBoundingClientRect стабильнее.
  const hostRect = host.getBoundingClientRect()
  const selectRect = select.getBoundingClientRect()
  const left = cssPxFromVisual(selectRect.left - hostRect.left)
  const top = cssPxFromVisual(selectRect.top - hostRect.top)
  const width = cssPxFromVisual(selectRect.width)
  const height = cssPxFromVisual(selectRect.height)

  wrap.style.position = 'absolute'
  wrap.style.left = `${left}px`
  wrap.style.top = `${top}px`
  wrap.style.width = `${width}px`
  wrap.style.maxWidth = `${width}px`
  wrap.style.height = `${height}px`
  wrap.style.overflow = 'visible'
  wrap.style.boxSizing = 'border-box'
  wrap.style.zIndex = '2'
}

/**
 * Нативный select ещё в вёрстке. Оверлей рядом с ним; при collapse карточки
 * скрываем явно (card-body { display: none } тоже прячет, но sync нужен
 * при частичном скрытии / нулевом боксе).
 *
 * @param {HTMLSelectElement | null | undefined} select
 * @returns {boolean}
 */
function isSourceSelectVisible(select) {
  if (!select?.isConnected) return false
  if (select.closest('.is-collapsed')) return false
  // Не опираемся на offsetParent: у visibility:hidden / некоторых предков он null,
  // хотя бокс есть — иначе оверлей прячется и «залипает» на старых координатах.
  const rect = select.getBoundingClientRect()
  if (rect.width <= 0 || rect.height <= 0) return false
  const style = window.getComputedStyle(select)
  if (style.display === 'none') return false
  return true
}

function hideFloatingWrap(wrap) {
  if (!wrap || wrap.style.display === 'none') return
  closeSelect(wrap)
  // display:none, не visibility: у открытого меню visibility:visible
  // перекрывает hidden родителя, и поле ещё рисуется поверх соседних.
  wrap.style.transition = 'none'
  wrap.style.visibility = 'hidden'
  wrap.style.pointerEvents = 'none'
  wrap.style.display = 'none'
}

function showFloatingWrap(wrap) {
  if (!wrap) return
  wrap.style.display = ''
  wrap.style.visibility = ''
  wrap.style.pointerEvents = ''
  wrap.style.transition = ''
}

function layoutFloatingWrap(select, wrap) {
  if (!select?.isConnected || !wrap) return

  if (isColumnFilterSelect(select)) {
    layoutColumnFilterWrap(select, wrap)
    return
  }

  if (!isSourceSelectVisible(select)) {
    hideFloatingWrap(wrap)
    return
  }

  layoutFormSelectWrap(select, wrap)
}

function attachFloatingWrap(select, wrap) {
  if (isColumnFilterSelect(select)) {
    layoutColumnFilterWrap(select, wrap)
    // Без scroll/resize listeners — элемент статичен в таблице.
    return
  }

  // Формы / пагинация: absolute в колонке (не fixed на body).
  layoutFloatingWrap(select, wrap)

  if (wrap._ltmsSelectResizeObserver) return

  // rAF: не менять layout синхронно в RO-колбэке (иначе Chrome кидает
  // «ResizeObserver loop completed with undelivered notifications»).
  let rafId = 0
  const reposition = () => {
    if (rafId) return
    rafId = requestAnimationFrame(() => {
      rafId = 0
      layoutFloatingWrap(select, wrap)
    })
  }
  wrap._ltmsSelectReposition = reposition

  if (typeof ResizeObserver !== 'undefined') {
    const ro = new ResizeObserver(reposition)
    ro.observe(select)
    // Ширина колонки меняется при flex-wrap — у самого select размер может не меняться.
    const host = select.parentElement
    if (host) ro.observe(host)
    wrap._ltmsSelectResizeObserver = ro
  }

  // Размер колонки / zoom — без scroll: оверлей едет с документом.
  window.addEventListener('resize', reposition)
  window.addEventListener(PAGE_ZOOM_EVENT, reposition)
  wrap._ltmsSelectDetachReposition = () => {
    window.removeEventListener('resize', reposition)
    window.removeEventListener(PAGE_ZOOM_EVENT, reposition)
    if (rafId) {
      cancelAnimationFrame(rafId)
      rafId = 0
    }
    wrap._ltmsSelectResizeObserver?.disconnect()
    wrap._ltmsSelectResizeObserver = null
    wrap._ltmsSelectReposition = null
    wrap._ltmsSelectDetachReposition = null
  }
}

function removeLegacyDom(select) {
  const wrap = getUiWrap(select)
  if (!wrap) return

  if (wrap.parentElement && wrap.parentElement !== document.body) {
    attachFloatingWrap(select, wrap)
    return
  }

  const legacySibling = select.nextElementSibling
  if (
    legacySibling instanceof HTMLElement &&
    legacySibling.classList.contains('custom-select-wrap') &&
    legacySibling.hasAttribute(UI_WRAP)
  ) {
    legacySibling._ltmsSelectCleanup?.()
    legacySibling.remove()
    select._ltmsSelectWrap = null
  }

  const legacyParent = select.closest('.custom-select-wrap')
  if (legacyParent && legacyParent !== wrap) {
    const parent = legacyParent.parentElement
    if (parent) {
      parent.insertBefore(select, legacyParent)
    }
    legacyParent._ltmsSelectCleanup?.()
    legacyParent.remove()
    select._ltmsSelectWrap = null
  }
}

function syncValidationState(select, wrap) {
  if (!select) return
  const trigger = wrap?.querySelector?.('.custom-select-trigger')

  const invalid =
    select.classList.contains('is-invalid') || select.hasAttribute('data-ltms-invalid')
  const valid = select.classList.contains('is-valid')

  if (!select.classList.contains('custom-select-hidden')) {
    select.classList.add('custom-select-hidden')
  }
  if (!select.hasAttribute(ENHANCED)) {
    select.setAttribute(ENHANCED, '1')
  }

  if (trigger) {
    if (trigger.classList.contains('is-invalid') !== invalid) {
      trigger.classList.toggle('is-invalid', invalid)
    }
    if (trigger.classList.contains('is-valid') !== valid) {
      trigger.classList.toggle('is-valid', valid)
    }
  }

  if (wrap) {
    if (wrap.classList.contains('is-invalid') !== invalid) {
      wrap.classList.toggle('is-invalid', invalid)
    }
    if (wrap.classList.contains('is-valid') !== valid) {
      wrap.classList.toggle('is-valid', valid)
    }
  }

  if (select.classList.contains('is-invalid') || select.classList.contains('is-valid')) {
    select.classList.remove('is-invalid', 'is-valid')
  }
}

function shouldEnhance(select) {
  if (!(select instanceof HTMLSelectElement)) return false

  removeLegacyDom(select)

  const existingWrap = getUiWrap(select)
  if (existingWrap) {
    attachFloatingWrap(select, existingWrap)
    syncValidationState(select, existingWrap)
    const menu = existingWrap._ltmsSelectMenu
    const labelSpan = existingWrap.querySelector?.('.custom-select-trigger__label')
    if (menu && labelSpan) {
      syncTriggerLabel(select, menu, labelSpan)
      if (!existingWrap._ltmsSelectStopValueSync) {
        startSelectValueSync(select, menu, labelSpan, existingWrap)
      }
    }
    return false
  }

  if (select.hasAttribute(ENHANCED)) return false
  if (select.classList.contains('custom-select-hidden')) return false
  if (
    !select.classList.contains('form-select') &&
    !select.classList.contains('apple-select')
  ) {
    return false
  }
  if (select.multiple) return false
  if (select.size > 1) return false
  if (select.closest(SKIP_CLOSEST)) return false
  if (select.dataset.choice !== undefined) return false
  if (select.getAttribute('aria-hidden') === 'true') return false
  return true
}

function syncDisabled(select, trigger, wrap) {
  const disabled = isSelectDisabled(select)
  trigger.disabled = disabled
  const isDisabled = wrap.classList.contains('is-disabled')
  if (isDisabled !== disabled) {
    wrap.classList.toggle('is-disabled', disabled)
  }
}

/**
 * Native select.disabled не отражает fieldset[disabled]; :disabled учитывает оба.
 *
 * @param {HTMLSelectElement} select Нативный select.
 * @returns {boolean} true, если выбор нельзя менять.
 */
function isSelectDisabled(select) {
  if (!select) {
    return true
  }
  // :disabled учитывает и атрибут, и fieldset[disabled] (в т.ч. после Edit).
  if (typeof select.matches === 'function' && select.matches(':disabled')) {
    return true
  }
  return Boolean(select.disabled || select.closest('fieldset[disabled]'))
}

function clearMenuPosition(menu) {
  menu.style.position = ''
  menu.style.left = ''
  menu.style.right = ''
  menu.style.top = ''
  menu.style.bottom = ''
  menu.style.width = ''
  menu.style.minWidth = ''
  menu.style.maxWidth = ''
  menu.style.zIndex = ''
  menu.style.maxHeight = ''
  menu.style.overflow = ''
  menu.style.overflowX = ''
  menu.style.overflowY = ''
  menu.style.marginTop = ''
  menu.style.boxSizing = ''
  menu.style.clipPath = ''
}

function positionMenu(trigger, menu) {
  return applyViewportMenu(trigger.getBoundingClientRect(), menu, { zIndex: '3000' })
}

function openSelect(wrap, trigger, menu) {
  wrap.classList.add('open')
  trigger.setAttribute('aria-expanded', 'true')

  menu.style.transition = 'none'

  // Меню всегда на body (fixed): иначе overflow:hidden/auto у
  // .home-block__table-wrap / .table-rtms обрезает список при короткой таблице
  // (пустой SoH после фильтра «Избранное = Да»). На масштабе 150% то же
  // обрезание даёт fieldset карточки.
  if (menu.parentElement !== document.body) {
    document.body.appendChild(menu)
  }
  const dropUp = positionMenu(trigger, menu)
  wrap.classList.toggle('drop-up', dropUp)
  menu.classList.toggle('drop-up', dropUp)

  void menu.offsetHeight
  menu.style.transition = ''
  menu.classList.add('is-open')
  // До .is-open меню ещё со scale/translate — пересчитать clip по итоговой рамке.
  clipBelowModuleHeader(menu)
  // Повтор после кадра: триггер/zoom иногда стабилизируются на тик позже.
  requestAnimationFrame(() => {
    if (!wrap.classList.contains('open')) return
    const nextDropUp = positionMenu(trigger, menu)
    wrap.classList.toggle('drop-up', nextDropUp)
    menu.classList.toggle('drop-up', nextDropUp)
    clipBelowModuleHeader(menu)
  })

  const reposition = () => {
    if (!wrap.classList.contains('open')) return
    const nextDropUp = positionMenu(trigger, menu)
    wrap.classList.toggle('drop-up', nextDropUp)
    menu.classList.toggle('drop-up', nextDropUp)
  }

  const detach = () => {
    window.removeEventListener('scroll', reposition, true)
    window.removeEventListener('resize', reposition)
    window.removeEventListener(PAGE_ZOOM_EVENT, reposition)
  }

  wrap._ltmsSelectDetachPosition = detach
  window.addEventListener('scroll', reposition, true)
  window.addEventListener('resize', reposition)
  window.addEventListener(PAGE_ZOOM_EVENT, reposition)
}

function closeSelect(wrap) {
  if (!wrap) return
  wrap._ltmsSelectDetachPosition?.()
  wrap._ltmsSelectDetachPosition = null
  wrap.classList.remove('open', 'drop-up')
  const trigger = wrap.querySelector('.custom-select-trigger')
  const menu = wrap._ltmsSelectMenu
  trigger?.setAttribute('aria-expanded', 'false')
  menu?.classList.remove('is-open', 'drop-up')
  if (menu) {
    menu.style.transition = 'none'
    clearMenuPosition(menu)
    if (menu.parentElement !== wrap) {
      wrap.appendChild(menu)
    }
    void menu.offsetHeight
    menu.style.transition = ''
  }
}

function resolveSelectedOption(select) {
  if (!(select instanceof HTMLSelectElement)) {
    return null
  }
  // Vue v-model выставляет option.selected / сверяет value; selectedIndex иногда отстаёт.
  const byValue = Array.from(select.options).find(opt => opt.value === select.value)
  if (byValue) {
    return byValue
  }
  if (select.selectedIndex >= 0) {
    return select.options[select.selectedIndex] || null
  }
  return null
}

function syncTriggerLabel(select, menu, labelSpan) {
  if (!labelSpan) {
    return
  }
  const selected = resolveSelectedOption(select)
  const text = selected ? selected.textContent ?? '' : ''
  if (labelSpan.textContent !== text) {
    labelSpan.textContent = text
  }
  if (!menu) {
    return
  }
  const value = select.value
  menu.querySelectorAll('.custom-select-option').forEach(el => {
    el.classList.toggle('active', el.getAttribute('data-value') === value)
  })
}

function rebuildOptions(select, menu, labelSpan) {
  menu.replaceChildren()

  Array.from(select.options).forEach((opt, index) => {
    const optEl = document.createElement('button')
    optEl.type = 'button'
    optEl.className = 'custom-select-option'
    optEl.setAttribute('data-value', opt.value)
    optEl.setAttribute('data-index', String(index))
    optEl.textContent = opt.textContent ?? ''

    if (opt.disabled) optEl.classList.add('disabled')
    menu.appendChild(optEl)
  })

  syncTriggerLabel(select, menu, labelSpan)
}

function bindOptionClick(select, menu, labelSpan, wrap) {
  menu.onclick = event => {
    const optEl = event.target.closest?.('.custom-select-option')
    if (!optEl || optEl.classList.contains('disabled')) return
    event.preventDefault()
    event.stopPropagation()

    const index = Number(optEl.getAttribute('data-index'))
    const opt = select.options[index]
    if (!opt || opt.disabled || isSelectDisabled(select)) return

    select.selectedIndex = index
    select.value = opt.value
    syncTriggerLabel(select, menu, labelSpan)
    closeSelect(wrap)
    select.dispatchEvent(new Event('input', { bubbles: true }))
    select.dispatchEvent(new Event('change', { bubbles: true }))
  }
}

/**
 * Vue после GET/PUT меняет option.selected без MutationObserver-событий на select.
 * Лёгкий rAF-sync держит подпись триггера в актуальном состоянии.
 */
function startSelectValueSync(select, menu, labelSpan, wrap) {
  wrap._ltmsSelectStopValueSync?.()

  let lastKey = null
  let raf = 0
  let running = true

  const tick = () => {
    if (!running) {
      return
    }
    if (!select.isConnected) {
      running = false
      return
    }
    const key = `${select.value}\0${select.selectedIndex}\0${select.options.length}`
    if (key !== lastKey) {
      lastKey = key
      syncTriggerLabel(select, menu, labelSpan)
    }
    raf = requestAnimationFrame(tick)
  }

  raf = requestAnimationFrame(tick)
  wrap._ltmsSelectStopValueSync = () => {
    running = false
    if (raf) {
      cancelAnimationFrame(raf)
      raf = 0
    }
    wrap._ltmsSelectStopValueSync = null
  }
}

function buildCustomSelect(select) {
  if (!shouldEnhance(select)) return

  const wrap = document.createElement('div')
  wrap.className = 'custom-select-wrap'
  wrap.setAttribute(UI_WRAP, '')
  wrap._ltmsSelectSource = select
  select._ltmsSelectWrap = wrap

  if (select.classList.contains('form-select-sm')) {
    wrap.classList.add('custom-select-wrap--sm')
  }
  if (select.classList.contains('w-auto')) {
    wrap.classList.add('custom-select-wrap--auto')
  }

  const trigger = document.createElement('button')
  trigger.type = 'button'
  trigger.className = 'custom-select-trigger'
  trigger.setAttribute('aria-haspopup', 'listbox')
  trigger.setAttribute('aria-expanded', 'false')

  const labelSpan = document.createElement('span')
  labelSpan.className = 'custom-select-trigger__label'
  trigger.appendChild(labelSpan)

  const chevron = document.createElement('i')
  chevron.className = 'bi bi-chevron-down'
  chevron.setAttribute('aria-hidden', 'true')
  trigger.appendChild(chevron)

  const menu = document.createElement('div')
  menu.className = 'custom-select-menu'
  menu.setAttribute('role', 'listbox')
  wrap._ltmsSelectMenu = menu

  wrap.appendChild(trigger)
  wrap.appendChild(menu)
  attachFloatingWrap(select, wrap)

  select.classList.add('custom-select-hidden')
  select.setAttribute(ENHANCED, '1')
  rebuildOptions(select, menu, labelSpan)
  bindOptionClick(select, menu, labelSpan, wrap)
  syncDisabled(select, trigger, wrap)
  syncValidationState(select, wrap)
  startSelectValueSync(select, menu, labelSpan, wrap)

  trigger.addEventListener('click', event => {
    event.preventDefault()
    event.stopPropagation()
    syncDisabled(select, trigger, wrap)
    if (trigger.disabled) return

    layoutFloatingWrap(select, wrap)
    const wasOpen = wrap.classList.contains('open')
    closeAllSelects(null)
    if (wasOpen) return

    openSelect(wrap, trigger, menu)
  })

  select.addEventListener('change', () => {
    syncTriggerLabel(select, menu, labelSpan)
  })

  let syncing = false
  const optionsObserver = new MutationObserver(mutations => {
    if (syncing) return

    const needsRebuild = mutations.some(
      m => m.type === 'childList' && m.target === select
    )
    const needsStateSync = mutations.some(
      m =>
        m.type === 'attributes' &&
        ['disabled', 'value', 'class', 'data-ltms-invalid'].includes(m.attributeName)
    )

    if (!needsRebuild && !needsStateSync) return

    syncing = true
    try {
      if (needsRebuild) {
        rebuildOptions(select, menu, labelSpan)
        bindOptionClick(select, menu, labelSpan, wrap)
      } else {
        syncTriggerLabel(select, menu, labelSpan)
      }
      syncDisabled(select, trigger, wrap)
      syncValidationState(select, wrap)
      layoutFloatingWrap(select, wrap)
    } finally {
      syncing = false
    }
  })
  optionsObserver.observe(select, {
    childList: true,
    attributes: true,
    attributeFilter: ['disabled', 'value', 'class', 'data-ltms-invalid'],
  })

  const fieldset = select.closest('fieldset')
  let fieldsetObserver = null
  if (fieldset) {
    fieldsetObserver = new MutationObserver(() => {
      syncDisabled(select, trigger, wrap)
      if (isSelectDisabled(select)) closeSelect(wrap)
    })
    fieldsetObserver.observe(fieldset, {
      attributes: true,
      attributeFilter: ['disabled'],
    })
  }

  wrap._ltmsSelectCleanup = () => {
    wrap._ltmsSelectStopValueSync?.()
    optionsObserver.disconnect()
    fieldsetObserver?.disconnect()
    wrap._ltmsSelectDetachReposition?.()
    wrap._ltmsSelectDetachPosition?.()
    if (menu.parentElement === document.body) menu.remove()
    if (wrap.parentElement) wrap.remove()
    if (select._ltmsSelectWrap === wrap) {
      select._ltmsSelectWrap = null
    }
  }
}

/** Сдвигает или прячет оверлеи после сворачивания карточек реестра. */
export function syncFloatingSelects() {
  document.querySelectorAll(`.custom-select-wrap[${UI_WRAP}]`).forEach(wrap => {
    const select = wrap._ltmsSelectSource
    if (select) layoutFloatingWrap(select, wrap)
  })
}

function closeAllSelects(except) {
  document.querySelectorAll('.custom-select-wrap.open').forEach(wrap => {
    if (wrap === except) return
    closeSelect(wrap)
  })
  // Меню могло остаться на body без класса open на wrap (после detach).
  document.querySelectorAll('.custom-select-menu.is-open').forEach(menu => {
    menu.classList.remove('is-open', 'drop-up')
    clearMenuPosition(menu)
  })
}

/** Закрыть все LTMS custom-select (в т.ч. при Back / confirm выхода). */
export function closeAllCustomSelects() {
  closeAllSelects(null)
}

function cleanupOrphans() {
  document.querySelectorAll(`.custom-select-wrap[${UI_WRAP}]`).forEach(wrap => {
    const select = wrap._ltmsSelectSource
    if (!select?.isConnected || getUiWrap(select) !== wrap) {
      wrap._ltmsSelectCleanup?.()
    }
  })

  document.querySelectorAll('.custom-select-menu.is-open').forEach(menu => {
    const stillLinked = [...document.querySelectorAll('.custom-select-wrap')].some(
      w => w._ltmsSelectMenu === menu
    )
    if (!stillLinked) menu.remove()
  })
}

let enhancing = false

export function enhanceCustomSelects(root = document) {
  if (!root?.querySelectorAll || enhancing) return
  enhancing = true
  try {
    cleanupOrphans()
    root.querySelectorAll('select').forEach(buildCustomSelect)
  } finally {
    enhancing = false
  }
}

export function startCustomSelects(root = document.body) {
  let scheduled = false
  let observer = null

  const run = () => {
    observer?.disconnect()
    try {
      enhanceCustomSelects(root)
    } finally {
      if (observer && root) {
        observer.observe(root, { childList: true, subtree: true })
      }
    }
  }

  const schedule = mutations => {
    if (scheduled || enhancing) return
    if (Array.isArray(mutations) && mutations.every(isInternalSelectMutation)) {
      return
    }

    scheduled = true
    queueMicrotask(() => {
      scheduled = false
      run()
    })
  }

  const onDocClick = event => {
    if (
      !event.target.closest?.('.custom-select-wrap, .custom-select-menu')
    ) {
      closeAllSelects(null)
    }
  }
  const onKeydown = event => {
    if (event.key === 'Escape') closeAllSelects(null)
  }
  const onNavClose = () => closeAllSelects(null)

  run()
  // После загрузки шрифтов/первого reflow колонки могут перенестись — подвинуть оверлеи.
  const syncAfterLayout = () => {
    requestAnimationFrame(() => syncFloatingSelects())
  }
  if (document.fonts?.ready) {
    document.fonts.ready.then(syncAfterLayout).catch(() => {})
  }
  syncAfterLayout()

  const onPageZoom = () => syncFloatingSelects()

  document.addEventListener('click', onDocClick, true)
  document.addEventListener('keydown', onKeydown)
  window.addEventListener('popstate', onNavClose)
  window.addEventListener('pagehide', onNavClose)
  window.addEventListener(PAGE_ZOOM_EVENT, onPageZoom)

  const onCollapseClass = mutations => {
    const collapsedToggled = mutations.some(mutation => {
      if (mutation.type !== 'attributes' || mutation.attributeName !== 'class') {
        return false
      }
      const el = mutation.target
      if (!(el instanceof Element)) return false
      const prev = mutation.oldValue || ''
      const next = typeof el.className === 'string' ? el.className : ''
      return prev.includes('is-collapsed') !== next.includes('is-collapsed')
    })
    // В том же кадре, до paint: rAF успевает нарисовать оверлеи на старых координатах.
    if (collapsedToggled) syncFloatingSelects()
  }

  let collapseObserver = null
  if (root && typeof MutationObserver !== 'undefined') {
    observer = new MutationObserver(schedule)
    observer.observe(root, { childList: true, subtree: true })

    collapseObserver = new MutationObserver(onCollapseClass)
    collapseObserver.observe(root, {
      attributes: true,
      attributeFilter: ['class'],
      attributeOldValue: true,
      subtree: true,
    })
  }

  return () => {
    document.removeEventListener('click', onDocClick, true)
    document.removeEventListener('keydown', onKeydown)
    window.removeEventListener('popstate', onNavClose)
    window.removeEventListener('pagehide', onNavClose)
    window.removeEventListener(PAGE_ZOOM_EVENT, onPageZoom)
    observer?.disconnect()
    collapseObserver?.disconnect()
    closeAllSelects(null)
  }
}
